/**
 * Utility for preloading images to improve user experience
 * @param {string[]} imageSources - Array of image URLs to preload
 * @returns {Promise<void>} - Promise that resolves when all images are preloaded
 */
export const preloadImages = (imageSources) => {
  const promises = imageSources.map((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = resolve;
      img.onerror = reject;
    });
  });

  return Promise.all(promises);
};

/**
 * Optimized image preloader that prioritizes visible images first
 * @param {string[]} criticalImages - Images needed immediately
 * @param {string[]} secondaryImages - Images that can be loaded after critical ones
 */
export const optimizedPreload = async (criticalImages = [], secondaryImages = []) => {
  try {
    // First load critical images
    await preloadImages(criticalImages);
    
    // Then load secondary images
    if (secondaryImages.length > 0) {
      // Use requestIdleCallback if available for non-critical images
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          preloadImages(secondaryImages);
        });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          preloadImages(secondaryImages);
        }, 200);
      }
    }
  } catch (error) {
    console.error('Error preloading images:', error);
  }
};

export default optimizedPreload;